import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseCardComponent } from '../../../../@fuse/components/card';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { EDIT_MODE_PATH } from '../../../features/admin/modules/modules-entity/templates/details/details/details.types';
import { filter, takeUntil } from 'rxjs/operators';
import { EMPTY, merge, type Observable } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InterfaceActionButtonComponent } from '../interface-action-button.component';
import type { InterfaceActionContext } from '../../../features/admin/modules/features/interface-actions/interface-actions.directive';
import { InterfaceActionsService } from '../../../features/admin/modules/features/interface-actions/interface-actions.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { pageLeadsWithLinkedItems } from './page-leading-linked-items.util';
import { SectionIconComponent } from './section-icon.component';
import { OVERVIEW_FIELD_TYPE, skipsInitialSelection } from './readonly/overview.types';
import { nearestScrollport } from '../../utils/scrollport.util';

@Component({
  selector: 'formly-field-pages',
  template: `
    <!-- Desktop sidebar -->
    <!-- Figma splits the 1136px content row as 265 rail + 74 gutter + 798 card. The rail used to be
         w-80 (320px) with a gap-4 (16px) gutter, which left the two panels almost touching. -->
    <div class="flex flex-col md:flex-row gap-4 md:gap-[74px] mb-4 min-w-0">
      <!-- top offset (.dd-rail in the styles below): stick below everything that already pins
           above the rail. That is the app shell's pinned top bar (--dd-sticky-header-offset, set
           on the document root while such a shell is mounted) plus the details page's sticky save
           toolbar (--dd-detail-toolbar-height, set on the details component host). Each is 0 when
           it does not apply: a shell without a pinned bar, or this field rendered outside a
           details page. Inside a container that scrolls itself the shell's part is left out
           (dd-own-scrollport, set once from the measured scrollport in the constructor). -->
      <fuse-card #rail class="dd-rail hidden md:flex flex-0 md:w-[265px] border self-start sticky z-10">

        <div class="w-full">
          @for (page of visiblePages(); track trackByFn($index, page)) {
            <!-- Keyless pages (the synthetic Overzicht page, #21239 task 21250) fall back to
                 props.dataCy; 'subpage-undefined' is a hook no spec can rely on. -->
            <button
              type="button"
              [attr.data-cy]="'subpage-' + (page.key ?? page.props?.dataCy ?? 'onbekend')"
              [class.dd-rail-item--active]="activePage() === page"
              (click)="goToPanel(page)"
              class="dd-rail-item w-full px-4 py-4 cursor-pointer first:border-t-0 border-t text-left">
              <span class="page-nav-label">{{ page.props.label }}</span>
            </button>
          }
        </div>
      </fuse-card>

      <!-- Mobile scrollable buttons -->
      <div class="block md:hidden w-full">
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-2 pb-2 min-w-min px-2 mt-2">
            @for (page of visiblePages(); track trackByFn($index, page)) {
              <!-- Same hook as the desktop rail. This used to be a plain data-cy attribute with
                   an interpolation in its value, which Angular compiles to a PROPERTY binding:
                   it threw NG0303 ("Can't bind to 'cy'") on every render in dev, and the mobile
                   rail was unreachable from a test. attr. is what writes an attribute. -->
              <button
                mat-flat-button
                [attr.data-cy]="'subpage-' + (page.key ?? page.props?.dataCy ?? 'onbekend')"
                [class.bg-primary]="activePage() === page"
                (click)="goToPanel(page)"
                class="outline-none ring-2 ring-primary small-button">
                {{ page.props.label }}
              </button>
            }
          </div>
        </div>
      </div>

      <!-- flex-1 does the sizing; the old w-[calc(100%-22rem)] hardcoded the previous rail width
           and fought it, which is why the card came out 4px wider than the rail+gutter allowed. -->
      <div class="w-full md:flex-1 min-w-0">
        @for (page of visiblePages(); track trackByFn($index, page)) {
          <!-- Identity, not key equality: the synthetic Overzicht page has no key, and
               undefined === undefined would open every keyless page at once. The selection
               always comes out of this very fieldGroup (ngOnInit / selectPage). -->
          @if (activePage() === page) {
            <!-- A page that renders its own cards (the Overzicht summary) is placed bare: wrapping
                 it in the entry card would put a card inside a card with a duplicated heading. -->
            @if (page.type === OVERVIEW_FIELD_TYPE) {
              <formly-field [field]="page"></formly-field>
            } @else {
              <fuse-card class="w-full min-w-0 border px-6 py-4">
                <div class="w-full">
                  <!-- A section that opens with a linked list draws its own heading (#22553): title
                       and add-button on one line, the search field under them, instead of a title
                       row with the whole toolbar stacked beneath it. Same predicate as the list. -->
                  @if (!pageLeadsWithLinkedItems(page)) {
                  <!-- Card header (Figma 2652-117626): icon + title belong here, not per section —
                       sections (below) only carry a label. -->
                  <div class="flex items-center justify-between gap-4 mb-4">
                    <div class="flex items-center gap-2 min-w-0">
                      @if (page.props.icon) {
                        <app-section-icon [icon]="page.props.icon" />
                      }
                      <!-- Name only. A per-section status lived here for a day (#21249/#21251) and
                           was pulled back out on 2026-09-17: between the header badge, the section
                           heading, the rail and the list column the same fact was on screen four
                           times, and a card that carries a section's status also renders that very
                           status field as one of its own rows. The entry's status is said once, in
                           the page header. -->
                      <h3 class="truncate">{{ page.props.label }}</h3>
                    </div>
                    <div class="flex items-center gap-2 flex-none">
                      @if (pageActionContext(page); as actionContext) {
                        <app-interface-action-button
                          class="flex-none"
                          [buttonData]="actionContext"
                          [context]="'formButton'"
                          buttonSize="small"
                          (handleClick)="executePageAction(page)">
                        </app-interface-action-button>
                      }
                      <!-- Edit THIS section (#21239, task 21249). The header's Bewerken button opens
                           whichever tab the form starts on; the pencil opens the one the reader is
                           looking at. Hidden entirely without edit rights — a disabled pencil would
                           promise something this reader can never do. -->
                      @if (detailsService.showEdit?.()) {
                        <button
                          type="button"
                          mat-icon-button
                          class="flex-none"
                          data-cy="section-edit-button"
                          [attr.aria-label]="'Bewerk ' + (page.props.label || 'deze sectie')"
                          [matTooltip]="'Bewerk ' + (page.props.label || 'deze sectie')"
                          (click)="editSection(page)">
                          <mat-icon class="icon-size-5" svgIcon="heroicons_outline:pencil-square"></mat-icon>
                        </button>
                      }
                    </div>
                  </div>
                  }
                  <formly-field [field]="page"></formly-field>
                </div>
              </fuse-card>
            }
          }
        }
      </div>
    </div>
  `,
  styles: `
    /* Where the rail pins, see the template comment. Its own scroll container's top edge already
       sits below the shell's bar, so there only the toolbar part remains; with the shell's part
       applied the rail sat 64px down and the content scrolled through the band above it. */
    .dd-rail {
      top: calc(var(--dd-sticky-header-offset, 0px) + var(--dd-detail-toolbar-height, 0px));
    }
    .dd-rail.dd-own-scrollport {
      top: var(--dd-detail-toolbar-height, 0px);
    }
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
    .overflow-x-auto {
      scroll-behavior: smooth;
    }
    /* Active page indicator (#20843): accent text + a filled accent plate, replacing the old
       underline. Figma "Section menu/tab", State=Active (node 818:38803): fill = brand
       primary @ 10% opacity over white, label = brand primary, no corner radius (the row
       keeps the existing bottom divider full-bleed). Uses the --fuse-primary(-rgb) chain so
       it tracks per-tenant branding, same alpha-ladder pattern as _table.scss row hover. */
    .dd-rail-item {
      transition:
        background-color 150ms ease,
        color 150ms ease;
    }
    /* Hover reuses the list-row brand-alpha ladder (_table.scss: 3% light / 6% dark) instead
       of the old static grey, so rail and list hover read as one system per tenant. */
    .dd-rail-item:hover:not(.dd-rail-item--active) {
      background-color: rgba(var(--fuse-primary-rgb), 0.03);
    }
    :host-context(.dark) .dd-rail-item:hover:not(.dd-rail-item--active) {
      background-color: rgba(var(--fuse-primary-rgb), 0.06);
    }
    .dd-rail-item--active {
      color: var(--fuse-primary);
      background-color: rgba(var(--fuse-primary-rgb), 0.1);
    }
    /* Dark needs a LIGHTER label, not the same brand colour: a tenant primary tuned for white
       (e.g. #3100FF) lands at 2:1 on its own dark plate and is unreadable. Figma shows the same
       tab in dark with a lightened tint (#9C9DFF over #211A40). Mixed off --fuse-primary rather
       than hardcoded, so it still tracks per-tenant branding — including tenants that already
       configure their own dark primary, where the mix stays close to what they picked. */
    :host-context(.dark) .dd-rail-item--active {
      color: color-mix(in srgb, var(--fuse-primary) 45%, #ffffff);
      background-color: rgba(var(--fuse-primary-rgb), 0.16);
    }
  `,
  imports: [
    FormlyModule,
    MatTabsModule,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    FuseCardComponent,
    InterfaceActionButtonComponent,
    SectionIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldPages extends FieldType implements OnInit {
  /** Template-visible so the panel can place the self-carding Overzicht page outside the card. */
  protected readonly OVERVIEW_FIELD_TYPE = OVERVIEW_FIELD_TYPE;
  protected readonly pageLeadsWithLinkedItems = pageLeadsWithLinkedItems;

  detailsService = inject(DETAILS_SERVICE_TOKEN) as DetailsService;
  selectedPage;
  cdr = inject(ChangeDetectorRef);
  private destroy$ = injectDestroy();
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #interfaceActionsService = inject(InterfaceActionsService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  /** The rail card; an Angular query, so not an ES-private field (the compiler forbids that). */
  private readonly rail = viewChild.required('rail', { read: ElementRef });

  constructor() {
    super();
    // `position: sticky` pins against the nearest scrolling ancestor. Inside a container that
    // scrolls itself (the form-builder preview under the admin page's mat-drawer-content) the
    // shell's bar is not above the rail's pin edge, so the rail leaves that offset out
    // (`.dd-rail.dd-own-scrollport`). Measured once, after the first render: the container does
    // not change while the field is mounted. Same rule as the detail toolbar.
    afterNextRender(() => {
      const rail = this.rail().nativeElement;
      if (nearestScrollport(rail)) {
        rail.classList.add('dd-own-scrollport');
      }
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  /**
   * The sections that exist for this reader right now. A method, not a snapshot: `hide` and
   * `className` are written by Formly expressions on every evaluation pass, so a filter taken once
   * in `ngOnInit` starts lying the moment a condition flips (same reason `tabs.component.ts` keeps
   * `visibleTabs()` a method).
   */
  visiblePages(): FormlyFieldConfig[] {
    return (this.field?.fieldGroup ?? []).filter(page => page.hide !== true && page.className !== 'hidden');
  }

  /**
   * The section the panel draws: the reader's own choice while it is still visible, otherwise the
   * one a fresh visit would have opened.
   *
   * Resolved at render time (review finding F3). `selectedPage` used to be a one-shot `ngOnInit`
   * snapshot, so a section hidden AFTER the first render — a conditional hide, or the card
   * header's own interface action changing the very entry that condition reads — left the
   * selection pointing at a field the rail no longer draws and this guard no longer matched: rail
   * on the left, empty column on the right, and no way back except a reload.
   */
  activePage(): FormlyFieldConfig | undefined {
    const visible = this.visiblePages();
    return this.selectedPage && visible.includes(this.selectedPage)
      ? this.selectedPage
      : this.#firstInitialPage(visible);
  }

  /**
   * The page the reader is meant to land on: the builder preview's leading Overzicht card is
   * flagged out of this, so arranging fields keeps showing the fields (#21239, task 21250). The
   * read view carries the same node unflagged and opens on it.
   */
  #firstInitialPage(visible: FormlyFieldConfig[]): FormlyFieldConfig | undefined {
    return visible.find(page => !skipsInitialSelection(page)) ?? visible[0];
  }

  ngOnInit() {
    const visiblePages = this.visiblePages();
    // Deep-link support (#20880): a shared/bookmarked URL can carry ?subpage=<key> to land on a
    // specific tab (e.g. "voorkeuren") instead of always defaulting to the first one.
    const requestedKey = this.#route.snapshot.queryParamMap.get('subpage');
    this.selectedPage =
      visiblePages.find(page => page.key === requestedKey) ?? this.#firstInitialPage(visiblePages);
    this.detailsService.updatePage$.next(this.selectedPage);
    // The detail page's one inbound channel for section switching — today the "Bekijk alles" link
    // on the read view's Overzicht card (#21239, task 21250). `tabs.component.ts` has always
    // listened; without this subscription the same link was dead on every pages-based module.
    // Optional-chained: the admin form preview and the specs provide a partial DetailsService.
    this.detailsService.switchTab$?.pipe(takeUntil(this.destroy$)).subscribe((pageKey: string) => {
      const page = this.field.fieldGroup?.find(
        candidate => String(candidate.key) === pageKey && candidate.hide !== true && candidate.className !== 'hidden'
      );
      if (page) {
        this.#selectPage(page);
      }
    });
    // Everything that can change WHICH sections exist, in one subscription.
    //
    // `formControl.valueChanges` alone is not it. A page's `hide` usually depends on `formState`
    // (the entry a condition tests, which an interface action on the card header rewrites), and
    // Formly re-evaluates expressions on a MODEL change anywhere in the form
    // (`checkExpressionOn: 'modelChange'`, formly-root-config.ts:40) — an Angular FormGroup
    // announces a value change to itself and its ancestors, never to a sibling, so a change
    // outside this block never reaches this control. For a KEYED page the ensuing
    // `changeHideState` unregisters its control and the block announces that, which is why this
    // used to look covered; a page that owns no control — the keyless Overzicht summary the read
    // view lands on — is only ever announced on `fieldChanges` (ngx-formly-core.mjs:579, :607,
    // :643-651).
    // Optional-chained: the admin form preview and the unit specs hand in a bare field config.
    const fieldChanges = this.field.options?.fieldChanges;
    const visibilityChanges$: Observable<unknown> = fieldChanges
      ? fieldChanges.pipe(filter(event => this.#changesPageVisibility(event)))
      : EMPTY;

    merge(this.field.formControl.valueChanges, visibilityChanges$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.#healSelection();
        this.cdr.markForCheck();
      });
  }

  /**
   * Whether a Formly field event is about one of MY sections appearing or disappearing.
   *
   * `fieldChanges` is one subject for the whole form, so most of what arrives is other fields'
   * business. `expressionChanges` carries the expression that was re-evaluated (`hide`,
   * `className`); `hidden` is what a parent's cascade emits for each child it closes.
   */
  #changesPageVisibility({
    field,
    type,
    property,
  }: {
    field: FormlyFieldConfig;
    type: string;
    property?: string;
  }): boolean {
    if (!this.field.fieldGroup?.includes(field)) {
      return false;
    }
    return type === 'hidden' || (type === 'expressionChanges' && (property === 'hide' || property === 'className'));
  }

  /**
   * Write the render-time fallback back into the stored selection, and tell the detail page about
   * it. `activePage()` alone would keep the panel right, but everything downstream of
   * `updatePage$` (the page's own notion of which section is open) would still be looking at the
   * hidden one.
   */
  #healSelection(): void {
    const resolved = this.activePage();
    if (resolved === this.selectedPage) {
      return;
    }
    this.selectedPage = resolved;
    this.detailsService.updatePage$.next(resolved);
  }

  /** Resolves the module carrying the page's configured card-header action (locked to its own form/module). */
  #pageActionModule(page: FormlyFieldConfig) {
    const moduleCode = page.props?.['interfaceActionModuleCode'];
    return moduleCode ? this.#modulesStore.enabledModules().find(m => m.code === moduleCode) : undefined;
  }

  /**
   * The configured card-header action for `page`, resolved and role-filtered — undefined hides the
   * button entirely (no action configured, or the current user's role excludes it).
   */
  pageActionContext(page: FormlyFieldConfig): InterfaceActionContext | undefined {
    const actionId = page.props?.['interfaceActionId'];
    if (!actionId) {
      return undefined;
    }
    // No saved entry (new, unsaved detail page) → no button. Without this gate the action
    // would run with entry undefined and handleCustomAction's id==null branch would CREATE
    // a phantom entry; mirrors the kebab menu's @if (entry()?.id) render gate.
    const entry = this.detailsService.entry();
    if (!entry?.id) {
      return undefined;
    }
    const action = this.#pageActionModule(page)?.interfaceActions?.find(a => a.id === actionId);
    // isAllowedForCurrentUser fails OPEN on a missing action (see its doc comment), so an unresolved
    // actionId must be rejected explicitly before that role check.
    if (!action || !this.#interfaceActionsService.isAllowedForCurrentUser(action)) {
      return undefined;
    }
    return { interfaceAction: action, entry, entries: [entry] };
  }

  executePageAction(page: FormlyFieldConfig): void {
    const context = this.pageActionContext(page);
    const module = this.#pageActionModule(page);
    if (!context || !module) {
      return;
    }
    // Same guard runner as the ⋮ menu (invalid-form gate + confirmation) — the two surfaces
    // offer the same actions, so they must not diverge (PR 9479 review B6).
    this.#interfaceActionsService.runWithGuards(
      context.interfaceAction,
      this.detailsService.form(),
      () =>
        this.#entriesService.processAction(
          { name: 'custom', id: context.entry?.id, entry: context.entry, interfaceAction: context.interfaceAction },
          module
        ),
      this.detailsService.fields()
    );
  }

  /**
   * The section card's pencil (#21239, task 21249): open the edit face ON THIS SECTION.
   *
   * One navigation, not "select the tab, then start editing": the edit face is a child route of
   * the same `:id`, and `?subpage` is what tells the rebuilt form which page to open — so both
   * belong in the same `navigate` call. Navigating them separately would leave a URL in between
   * that a refresh could land on with the other page selected.
   *
   * A keyless page (the synthetic Overzicht node) renders its own cards and never carries a
   * pencil; `null` is kept here anyway so the param is CLEARED rather than left pointing at the
   * previous page, which is what `undefined` under `merge` would do.
   */
  editSection(page: FormlyFieldConfig): void {
    this.#router.navigate([EDIT_MODE_PATH], {
      relativeTo: this.#route,
      queryParams: { subpage: page.key ?? null },
      queryParamsHandling: 'merge',
    });
  }

  goToPanel(page: FormlyFieldConfig) {
    // Optional-chained on the implicit global `event`: outside a real click (a spec, a
    // programmatic caller) there is none, and the bare call threw a TypeError.
    event?.preventDefault();
    this.#selectPage(page);
  }

  /**
   * The selection itself, without the click. `goToPanel` is a template handler and calls
   * `event.preventDefault()` on the implicit global event; a programmatic caller (the Overzicht
   * card's "Bekijk alles", below) has no event to prevent and would blow up on it.
   */
  #selectPage(page: FormlyFieldConfig): void {
    this.selectedPage = page;
    this.detailsService.updatePage$.next(this.selectedPage);
    this.cdr.markForCheck();
    // Keep ?subpage in sync so the active tab is shareable/bookmarkable and survives a refresh.
    // A page WITHOUT a key (the synthetic Overzicht page) must CLEAR the param — `null` removes
    // it, whereas `undefined` under queryParamsHandling: 'merge' would leave the previous page's
    // key in the URL and send a refresh to the wrong panel.
    // replaceUrl: a tab click is not a navigation the user expects on the back-button stack.
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { subpage: page.key ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
